import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, Check, Footprints, Crosshair, Target, BarChart3, Sparkles,
  Radio, Tv, Smartphone, Layout, Monitor, Headphones, ThumbsUp, Mail,
  Store, ShoppingBag, Stethoscope, Building2, Plane, Briefcase, Car, Coffee,
  Award, ChevronDown, Megaphone, Activity, Eye, Layers,
  Scissors, GraduationCap, Wrench, Package, Home
} from "lucide-react";
import { SEO } from "../components/SEO";
import { CreditOfferModal } from "../components/CreditOfferModal";
import { LatestBlogPosts } from "../components/LatestBlogPosts";

// ---------- Top Promo Banner ----------
const PromoBanner = ({ onClaim }) => (
  <div className="bg-gradient-to-r from-orange-primary via-orange-secondary to-orange-primary text-white text-center py-3 px-4" data-testid="ads-promo-banner">
    <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-sm md:text-base">
      <span className="font-semibold">🎁 Get a $500 credit to kickstart your first SmartReach™ campaign!</span>
      <button
        onClick={onClaim}
        className="font-semibold underline underline-offset-2 hover:no-underline inline-flex items-center gap-1"
        data-testid="ads-promo-claim-btn"
      >
        See terms & claim <ArrowRight size={14} />
      </button>
    </div>
  </div>
);

// ---------- Hero ----------
const HERO_PROPS = [
  "100% Self-serve",
  "No contracts",
  "No minimum spend",
  "5-minute setup"
];

const HERO_OUTCOMES = [
  { icon: Eye, label: "Awareness", color: "from-blue-500 to-cyan-400" },
  { icon: Footprints, label: "Store Visits", color: "from-orange-primary to-orange-secondary" },
  { icon: Activity, label: "Engagement", color: "from-purple-500 to-pink-400" }
];

const HeroSection = ({ onLaunch }) => (
  <section className="section-padding bg-gradient-to-br from-orange-light via-white to-white" data-testid="ads-hero">
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="inline-flex items-center gap-2 text-orange-primary font-semibold text-sm mb-4 bg-white px-4 py-2 rounded-full shadow-sm">
            <Sparkles size={16} /> SmartReach™ Ads Manager
          </span>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-gray-dark mb-6 leading-tight">
            Start driving <span className="text-gradient-orange">real business results</span> in minutes.
          </h1>
          <p className="text-lg md:text-xl text-gray-medium mb-6">
            The self-serve SmartReach™ platform for driving measurable outcomes — foot traffic, calls,
            and sales — with hyperlocal ads across radio, mobile, CTV, audio, DOOH, and more.
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-2 mb-8">
            {HERO_PROPS.map((p) => (
              <div key={p} className="flex items-center gap-2 text-gray-dark font-medium">
                <Check size={18} className="text-orange-primary" />
                <span>{p}</span>
              </div>
            ))}
          </div>
          <button onClick={onLaunch} className="btn-primary text-lg" data-testid="ads-hero-launch-btn">
            Launch My Campaign <ArrowRight size={20} className="inline ml-2" />
          </button>
        </div>

        {/* Outcome cards / mock dashboard */}
        <div className="relative">
          <div className="grid grid-cols-3 gap-4">
            {HERO_OUTCOMES.map((o) => (
              <div key={o.label} className="bg-white rounded-2xl p-5 shadow-card text-center card-hover">
                <div className={`w-12 h-12 mx-auto rounded-xl bg-gradient-to-br ${o.color} flex items-center justify-center mb-3`}>
                  <o.icon className="text-white" size={24} />
                </div>
                <p className="font-display font-bold text-gray-dark text-sm">{o.label}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 bg-gradient-to-br from-gray-dark to-gray-900 rounded-2xl p-6 text-white shadow-2xl">
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-white/10">
              <div>
                <p className="text-xs text-gray-400">Live Campaign</p>
                <p className="font-display font-bold">Lunch Rush — Store #4</p>
              </div>
              <span className="text-xs bg-green-500/20 text-green-400 px-3 py-1 rounded-full">RUNNING</span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: "Visits", val: "1,284" },
                { label: "Cost / Visit", val: "$1.92" },
                { label: "Lift", val: "+34%" }
              ].map((k) => (
                <div key={k.label}>
                  <p className="text-xs text-gray-400">{k.label}</p>
                  <p className="text-xl font-display font-bold text-orange-secondary">{k.val}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Trust badges */}
      <div className="mt-14 flex flex-wrap items-center justify-center gap-6 opacity-80">
        {["G2 — High Performer", "Capterra — 4.7★", "G2 — Easiest Setup", "G2 — Best ROI"].map((b) => (
          <div key={b} className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100">
            <Award size={16} className="text-orange-primary" />
            <span className="text-sm font-medium text-gray-dark">{b}</span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ---------- 1-2-3 Steps ----------
const STEPS = [
  {
    num: "1",
    title: "Choose Your Goal",
    desc: "Whether it's awareness, foot traffic, or sales, we guide you to set up your campaign for success in just a few clicks.",
    icon: Target
  },
  {
    num: "2",
    title: "Reach The Right People",
    desc: "Use real-world insights — location, behavior, and demographics — to reach the most relevant audiences for your business.",
    icon: Crosshair
  },
  {
    num: "3",
    title: "Track & Optimize Live",
    desc: "Watch performance in real time. Optimize creatives, audiences, and budget along the way to maximize impact.",
    icon: BarChart3
  }
];

const StepsSection = ({ onLaunch }) => (
  <section className="section-padding bg-white" data-testid="ads-steps-section">
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-12">
        <h2 className="font-display text-3xl md:text-5xl font-bold text-gray-dark mb-4 leading-tight">
          Launching your campaign is as easy as <span className="text-gradient-orange">1 — 2 — 3.</span>
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
        {STEPS.map((step) => (
          <div key={step.num} className="relative bg-gradient-to-br from-orange-light to-white rounded-2xl p-8 border border-orange-primary/10 card-hover" data-testid={`ads-step-${step.num}`}>
            <div className="flex items-start gap-4 mb-4">
              <span className="text-7xl font-display font-bold text-orange-primary leading-none">{step.num}</span>
              <div className="w-12 h-12 rounded-xl gradient-orange flex items-center justify-center mt-2">
                <step.icon className="text-white" size={24} />
              </div>
            </div>
            <h3 className="font-display font-bold text-xl text-gray-dark mb-2">{step.title}</h3>
            <p className="text-gray-medium">{step.desc}</p>
          </div>
        ))}
      </div>

      <div className="bg-gradient-to-br from-gray-dark via-gray-900 to-gray-dark rounded-3xl p-10 md:p-14 text-white text-center">
        <h3 className="font-display text-2xl md:text-4xl font-bold mb-4">Ready? Let's launch your campaign in minutes.</h3>
        <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
          Getting started with SmartReach™ Ads Manager is a breeze. No headaches, no hassle.
        </p>
        <button onClick={onLaunch} className="btn-primary text-lg" data-testid="ads-steps-cta-btn">
          Launch My First Campaign <ArrowRight size={20} className="inline ml-2" />
        </button>
      </div>
    </div>
  </section>
);

// ---------- All Screens / Channels grid ----------
const CHANNELS = [
  { icon: Radio, label: "Radio" },
  { icon: Tv, label: "Connected TV" },
  { icon: Smartphone, label: "Mobile" },
  { icon: Monitor, label: "Display" },
  { icon: Headphones, label: "Audio / Podcast" },
  { icon: Layout, label: "DOOH" },
  { icon: ThumbsUp, label: "Social" },
  { icon: Mail, label: "Direct Mail" }
];

const AllScreensSection = () => (
  <section className="section-padding bg-gray-light" data-testid="ads-all-screens-section">
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-12">
        <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">
          Run your ads across <span className="text-gradient-orange">all screens and devices</span>
        </h2>
        <p className="text-gray-medium max-w-2xl mx-auto text-lg">
          One platform. Every channel your customers actually use.
        </p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {CHANNELS.map((c) => (
          <div key={c.label} className="bg-white p-6 rounded-2xl shadow-card card-hover text-center">
            <c.icon className="text-orange-primary mx-auto mb-3" size={36} />
            <p className="font-display font-semibold text-gray-dark">{c.label}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ---------- Channel deep-dives ----------
const DEEP_DIVES = [
  {
    tag: "VIDEO & CTV",
    icon: Tv,
    title: "Advertise on the Big Screen With CTV ads",
    desc: "Supercharge your campaigns with video and CTV ads on top streaming platforms — deliver your message where attention is at its peak.",
    desc2: "With precise location-based targeting and real-time tracking, connect with the right customers and drive measurable results from one easy-to-use platform.",
    cta: "Launch Video Ads",
    flip: false
  },
  {
    tag: "PODCAST & DIGITAL AUDIO",
    icon: Headphones,
    title: "Turn Listeners Into Customers with Targeted Audio Ads",
    desc: "Reach your audience with the power of sound. Run audio ads on top podcasts, streaming platforms, and the FunAsia radio network to connect with customers in a personal, impactful way.",
    desc2: "Track in-store visits and online conversions straight from your audio ads. Proof that your message isn't just heard — it's acted on.",
    cta: "Launch Audio Ads",
    flip: true
  },
  {
    tag: "MOBILE",
    icon: Smartphone,
    title: "Right People. Right Time. Right Place.",
    desc: "Reach your audience where they're most engaged — on their mobile devices. Advanced geo-location technology targets consumers within or near your business locations.",
    desc2: "Or connect with users based on real-world behavior — like people who've recently visited your competitors, or specific places that signal intent.",
    cta: "Launch Mobile Ads",
    flip: false
  }
];

const ChannelDeepDive = ({ d, onLaunch }) => (
  <section className={`section-padding ${d.flip ? "bg-gray-light" : "bg-white"}`} data-testid={`ads-deepdive-${d.tag.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="max-w-7xl mx-auto">
      <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${d.flip ? "lg:[&>*:first-child]:order-2" : ""}`}>
        <div>
          <span className="inline-flex items-center gap-2 text-orange-primary font-semibold text-xs uppercase tracking-wider mb-4">
            <d.icon size={14} /> {d.tag}
          </span>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4 leading-tight">{d.title}</h2>
          <p className="text-gray-medium text-lg mb-4">{d.desc}</p>
          <p className="text-gray-medium mb-6">{d.desc2}</p>
          <button onClick={onLaunch} className="btn-primary">
            {d.cta} <ArrowRight size={18} className="inline ml-2" />
          </button>
        </div>
        <div className="relative">
          <div className="aspect-square bg-gradient-to-br from-orange-primary to-orange-secondary rounded-3xl p-8 flex items-center justify-center relative overflow-hidden shadow-2xl">
            <div className="absolute inset-0 opacity-10 pointer-events-none">
              {[...Array(5)].map((_, i) => (
                <div
                  key={`ring-${i}`}
                  className="absolute border-2 border-white rounded-full"
                  style={{
                    width: `${(i + 1) * 100}px`,
                    height: `${(i + 1) * 100}px`,
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)"
                  }}
                />
              ))}
            </div>
            <div className="relative text-center text-white z-10">
              <d.icon size={96} className="mx-auto mb-4" strokeWidth={1.5} />
              <p className="font-display text-3xl font-bold">{d.tag}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ---------- Industries ----------
// Each entry maps to a slug under /industries/:slug. Slugs must match
// /app/frontend/src/data/industries.js so links render content correctly.
const INDUSTRIES = [
  { icon: Briefcase, name: "Ad Agencies", slug: "ad-agency" },
  { icon: Car, name: "Auto Dealers", slug: "automotive-dealers" },
  { icon: Wrench, name: "Home & Auto Services", slug: "home-auto-services" },
  { icon: Package, name: "Consumer Goods", slug: "consumer-goods" },
  { icon: Stethoscope, name: "Medical & Healthcare", slug: "healthcare" },
  { icon: Store, name: "Retail", slug: "retail" },
  { icon: Coffee, name: "Restaurants & Cafés", slug: "restaurants" },
  { icon: Home, name: "Real Estate", slug: "real-estate" },
  { icon: Scissors, name: "Salons, Spas & Wellness", slug: "salons-spas-wellness" },
  { icon: GraduationCap, name: "Education & Coaching", slug: "education" },
  { icon: Megaphone, name: "Events & Entertainment", slug: "entertainment" },
  { icon: Building2, name: "Multi-Location Brands", slug: "multi-location-brands" },
  { icon: Plane, name: "Travel & Tourism", slug: "travel-tourism" }
];

const IndustriesSection = () => (
  <section className="section-padding bg-white" data-testid="ads-industries-section">
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-12">
        <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">
          Built to help businesses <span className="text-gradient-orange">across all industries</span>
        </h2>
        <p className="text-gray-medium max-w-3xl mx-auto text-lg">
          Whether you're promoting a restaurant, retail store, healthcare service, or travel destination,
          SmartReach™ Ads Manager ensures your message reaches the right people at the right time.
        </p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
        {INDUSTRIES.map((ind) => (
          <Link
            key={ind.slug}
            to={`/industries/${ind.slug}`}
            className="group bg-gradient-to-br from-orange-light to-white p-5 rounded-2xl border border-orange-primary/10 hover:border-orange-primary/40 hover:shadow-card transition-all duration-200 text-center block"
            data-testid={`home-industry-${ind.slug}`}
          >
            <div className="w-12 h-12 rounded-xl bg-white/70 group-hover:bg-orange-primary/10 flex items-center justify-center mx-auto mb-3 transition-colors">
              <ind.icon className="text-orange-primary" size={26} />
            </div>
            <p className="text-xs md:text-sm font-display font-semibold text-gray-dark group-hover:text-orange-primary transition-colors">
              {ind.name}
            </p>
            <span className="text-[11px] text-orange-primary/0 group-hover:text-orange-primary mt-2 inline-flex items-center gap-1 transition-all">
              See solution <ArrowRight size={11} />
            </span>
          </Link>
        ))}
      </div>
      <div className="text-center mt-10">
        <Link
          to="/industries"
          className="inline-flex items-center gap-2 text-orange-primary font-semibold hover:gap-3 transition-all"
          data-testid="home-see-all-industries"
        >
          See all industry strategies <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  </section>
);

// ---------- FAQ ----------
const FAQS = [
  {
    q: "What is SmartReach™ Ads Manager?",
    a: "SmartReach™ Ads Manager is FunAsia Digital's self-serve advertising platform that helps businesses target audiences based on real-world behavior. It lets advertisers leverage data-driven insights to optimize campaigns and drive measurable outcomes — foot traffic, in-store visits, awareness, website traffic, conversions, and incremental sales."
  },
  {
    q: "How does geo-fencing work in Ads Manager?",
    a: "Geo-fencing lets you target specific geographic areas — locations near your business or competitor locations. Location-based ads are triggered when users enter or exit a designated area, enabling precise targeting based on real-world movement. You can also target audiences based on past visitation behaviors to locations of interest."
  },
  {
    q: "Can I target specific industries or business types?",
    a: "Yes — Ads Manager offers industry-specific audience targeting. Whether you're in retail, hospitality, education, healthcare, real estate, or any other sector, the platform helps you reach the right customers by tapping into their real-world behaviors."
  },
  {
    q: "Can I target competitors' customers?",
    a: "Yes! With our conquesting capabilities, you can reach customers who recently visited your competitors' locations. This powerful strategy draws attention away from the competition and brings their audience directly to you."
  },
  {
    q: "Can I run campaigns for multiple locations at once?",
    a: "Absolutely. Run campaigns across multiple locations simultaneously — specific stores, regions, or entire cities. Customize ad delivery to the exact locations that matter most for your business, as local or as broad as you need."
  },
  {
    q: "How do I measure success and what reporting is provided?",
    a: "Success is measured by real-world actions of your audience. Robust reporting tracks key metrics — in-store visits, conversion rates, cost per visit, and visit lift. You can directly correlate ad spend to business outcomes and adjust strategy in real time for maximum ROI."
  },
  {
    q: "What is the pricing for SmartReach™ Ads Manager?",
    a: "You decide how much to spend based on your budget, goals, and targeting preferences. There are no minimum spend requirements and you can pause or adjust spending at any time with zero commitment. Cost is determined by impressions, geo-fencing areas, and targeting selections — you're always in full control."
  },
  {
    q: "Are there any setup fees?",
    a: "No hidden setup fees. The platform offers a seamless onboarding experience and you only pay for the ads you run based on your campaign budget and targeting selections."
  },
  {
    q: "How does FunAsia Digital ensure privacy in ad targeting?",
    a: "We adhere to strict privacy standards. Aggregated, anonymized data is used to build audience segments — no personally identifiable information is used in targeting — while still delivering precise and effective campaigns."
  },
  {
    q: "How do I get started?",
    a: "Easy! Sign up for an account, get access to tutorials and resources, and launch your first campaign in minutes. Plus claim your $500 starter credit when you spend $1,000 in your first 60 days."
  },
  {
    q: "Can I work with an ad agency to manage my campaigns?",
    a: "Of course. Ads Manager is agency-friendly. You can give your agency full access to your account so they can optimize campaigns, adjust targeting, and monitor performance, while you retain full control over budget and goals."
  }
];

const FAQItem = ({ faq, isOpen, onToggle }) => (
  <div className="bg-white rounded-xl shadow-card overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full px-6 py-5 text-left flex items-center justify-between gap-4 hover:bg-gray-50 transition-colors"
      data-testid={`ads-faq-q-${faq.q.slice(0, 20).replace(/\s+/g, "-").toLowerCase()}`}
    >
      <span className="font-display font-semibold text-gray-dark">{faq.q}</span>
      <ChevronDown size={20} className={`text-orange-primary flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
    </button>
    {isOpen && (
      <div className="px-6 pb-5">
        <p className="text-gray-medium leading-relaxed">{faq.a}</p>
      </div>
    )}
  </div>
);

const FAQSection = () => {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section className="section-padding bg-gray-light" data-testid="ads-faq-section">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">
            Get the answers you need about <span className="text-gradient-orange">Ads Manager</span>
          </h2>
          <p className="text-gray-medium text-lg">
            Whether you're driving more store visits, understanding audience targeting, or tracking
            performance, these FAQs cover the essentials.
          </p>
        </div>
        <div className="space-y-3">
          {FAQS.map((faq, idx) => (
            <FAQItem
              key={faq.q}
              faq={faq}
              isOpen={openIdx === idx}
              onToggle={() => setOpenIdx(openIdx === idx ? null : idx)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// ---------- Final Credit Offer ----------
const CREDIT_PROPS = ["100% Self-serve", "No contracts", "No minimum spend", "5-minute setup"];

const CreditOfferSection = ({ onClaim }) => (
  <section className="section-padding gradient-orange" data-testid="ads-credit-section">
    <div className="max-w-6xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
        <div className="lg:col-span-2 text-white">
          <h2 className="font-display text-3xl md:text-4xl font-bold mb-4 leading-tight">
            Get a $500 credit to kickstart your campaign!
          </h2>
          <p className="text-white/90 text-lg mb-6">
            Sign up for SmartReach&trade; Ads Manager and receive a $500 credit after you spend $1,000
            within 60 days of creating your account. Reach the right customers, drive more store
            visits, and see real business results.
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-2 mb-6">
            {CREDIT_PROPS.map((p) => (
              <div key={p} className="flex items-center gap-2 text-white/95 font-medium">
                <Check size={18} />
                <span>{p}</span>
              </div>
            ))}
          </div>
          <button
            onClick={onClaim}
            className="bg-white text-orange-primary px-8 py-4 rounded-full font-semibold text-lg hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
            data-testid="ads-credit-launch-btn"
          >
            Claim Your $500 Credit <ArrowRight size={20} className="inline ml-2" />
          </button>
        </div>

        <button
          onClick={onClaim}
          className="bg-white/15 backdrop-blur-sm rounded-3xl p-8 border-2 border-dashed border-white/40 text-center text-white hover:bg-white/20 transition-all cursor-pointer block w-full"
          data-testid="ads-credit-card-btn"
        >
          <p className="text-sm uppercase tracking-wider opacity-80 mb-2">Starter Credit</p>
          <p className="font-display text-6xl md:text-7xl font-bold mb-2">$500</p>
          <p className="text-white/80 text-sm">After $1,000 ad spend in first 60 days</p>
          <p className="mt-4 inline-flex items-center gap-1 text-sm font-semibold underline underline-offset-2">
            View terms &amp; claim <ArrowRight size={14} />
          </p>
        </button>
      </div>
    </div>
  </section>
);

// ---------- Page ----------
const AdsManagerPage = () => {
  const navigate = useNavigate();
  const [creditModalOpen, setCreditModalOpen] = useState(false);
  const launch = () => navigate("/contact");
  const openCreditModal = () => setCreditModalOpen(true);

  // FAQ schema.org JSON-LD — enables rich Q&A snippets in Google search results
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQS.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a }
    }))
  };

  // Organization schema — boosts brand knowledge graph
  const orgJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "FunAsia Digital",
    "url": "https://funasiadigital.com",
    "logo": "https://customer-assets.emergentagent.com/job_1b7e3ff3-a6f2-4f8b-ba70-0b0056d06e3d/artifacts/arydq0cs_funasi%20digital.png",
    "description": "FunAsia Digital — SmartReach platform for driving foot traffic and real business results.",
    "sameAs": []
  };

  return (
    <div className="pt-20" data-testid="ads-manager-page">
      <SEO
        title="Drive Real Customers In Minutes — SmartReach™ Ads Manager"
        description="Start driving real business results in minutes. SmartReach™ Ads Manager is the self-serve platform for foot traffic, sales, and awareness — across radio, mobile, CTV, audio, DOOH, and more. No contracts, no minimum spend."
        path="/"
      >
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      </SEO>

      <PromoBanner onClaim={openCreditModal} />
      <HeroSection onLaunch={launch} />
      <StepsSection onLaunch={launch} />
      <AllScreensSection />
      {DEEP_DIVES.map((d) => (
        <ChannelDeepDive key={d.tag} d={d} onLaunch={launch} />
      ))}
      <IndustriesSection />
      <LatestBlogPosts limit={3} />
      <FAQSection />
      <CreditOfferSection onClaim={openCreditModal} />

      <CreditOfferModal open={creditModalOpen} onOpenChange={setCreditModalOpen} />
    </div>
  );
};

export default AdsManagerPage;
